import { useEffect, useState, useCallback } from "react";
import { getAuditLog, AuditEntry } from "../../../api/api-hook";

const BRAND_GREEN = "#023822";
const BRAND_GOLD  = "#C9A24D";
const PAGE_SIZE   = 50;

// ── Per-action display metadata ───────────────────────────────────────────────
const ACTION_META: Record<string, { icon: string; color: string; bg: string }> = {
  login:                { icon: "bi-box-arrow-in-right",     color: "#0d6efd", bg: "rgba(13,110,253,0.1)"  },
  package_change:       { icon: "bi-arrow-left-right",       color: "#023822", bg: "rgba(2,56,34,0.1)"    },
  refund:               { icon: "bi-arrow-counterclockwise", color: "#dc3545", bg: "rgba(220,53,69,0.1)"  },
  contact_update:       { icon: "bi-person-check",           color: "#6f42c1", bg: "rgba(111,66,193,0.1)" },
  purchase:             { icon: "bi-bag-check",              color: "#198754", bg: "rgba(25,135,84,0.1)"  },
  bag_tag:              { icon: "bi-tag",                    color: "#fd7e14", bg: "rgba(253,126,20,0.1)" },
  card_type:            { icon: "bi-credit-card",            color: "#0dcaf0", bg: "rgba(13,202,240,0.1)" },
  redemption_reversal:  { icon: "bi-x-circle",              color: "#dc3545", bg: "rgba(220,53,69,0.1)"  },
  replacement_card:     { icon: "bi-credit-card-2-front",   color: "#6610f2", bg: "rgba(102,16,242,0.1)" },
  bronze_addon:         { icon: "bi-plus-circle",            color: "#fd7e14", bg: "rgba(253,126,20,0.1)" },
  page_content:         { icon: "bi-file-earmark-text",     color: "#20c997", bg: "rgba(32,201,151,0.1)" },
  bulk_action:          { icon: "bi-stack",                  color: "#0d6efd", bg: "rgba(13,110,253,0.1)" },
  bulk_upload:          { icon: "bi-cloud-upload",            color: "#0d6efd", bg: "rgba(13,110,253,0.1)" },
  promo_code:           { icon: "bi-tag-fill",               color: "#198754", bg: "rgba(25,135,84,0.1)"  },
  default:              { icon: "bi-activity",               color: "#6c757d", bg: "rgba(108,117,125,0.1)"},
};

function formatFull(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true,
  });
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const diffMs   = Date.now() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1)  return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24)  return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7)   return `${diffDays} days ago`;
  return "";
}

// ── Action filter options ─────────────────────────────────────────────────────
const ACTION_FILTERS = [
  { value: "",                    label: "All actions" },
  { value: "login",               label: "Logins" },
  { value: "purchase",            label: "Purchases" },
  { value: "package_change",      label: "Package changes" },
  { value: "refund",              label: "Refunds" },
  { value: "contact_update",      label: "Contact updates" },
  { value: "bag_tag",             label: "Bag tag orders" },
  { value: "replacement_card",    label: "Replacement cards" },
  { value: "bronze_addon",        label: "Bronze add-ons" },
  { value: "redemption_reversal", label: "Redemption reversals" },
  { value: "promo_code",          label: "Promo codes" },
  { value: "page_content",        label: "Page content edits" },
  { value: "bulk_action",         label: "Bulk actions" },
  { value: "bulk_upload",         label: "Bulk order uploads" },
];

// ── Component ────────────────────────────────────────────────────────────────
const AuditLog = () => {
  const [entries,      setEntries]      = useState<AuditEntry[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [actionFilter, setActionFilter] = useState("");
  const [actorFilter,  setActorFilter]  = useState("");
  const [search,       setSearch]       = useState("");
  const [page,         setPage]         = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await getAuditLog();
    setEntries(data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const distinctActors = Array.from(new Set(entries.map((e) => e.actor).filter(Boolean))).sort();

  const filtered = entries.filter((e) => {
    if (actionFilter && e.action !== actionFilter) return false;
    if (actorFilter  && e.actor  !== actorFilter)  return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        e.label.toLowerCase().includes(q)   ||
        e.details.toLowerCase().includes(q) ||
        e.actor.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage    = Math.min(page, totalPages);
  const pageEntries = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [actionFilter, actorFilter, search]);

  // ── Pagination helper ─────────────────────────────────────────────────────
  const pageNumbers = (): (number | "…")[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const nums: (number | "…")[] = [1];
    if (safePage > 3) nums.push("…");
    for (let p = Math.max(2, safePage - 1); p <= Math.min(totalPages - 1, safePage + 1); p++) nums.push(p);
    if (safePage < totalPages - 2) nums.push("…");
    nums.push(totalPages);
    return nums;
  };

  return (
    <div>
      {/* ── Page header ───────────────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
        <div>
          <h4 className="fw-bold mb-0" style={{ color: BRAND_GREEN }}>
            <i className="bi bi-clock-history me-2" style={{ color: BRAND_GOLD }} />
            Audit Log
          </h4>
          <p className="text-muted small mb-0 mt-1">
            A record of all admin actions — newest first.
          </p>
        </div>
        <button
          className="btn btn-sm"
          onClick={load}
          disabled={loading}
          style={{ backgroundColor: BRAND_GREEN, color: "#fff", border: "none" }}
        >
          {loading
            ? <><span className="spinner-border spinner-border-sm me-1" role="status" /> Refreshing…</>
            : <><i className="bi bi-arrow-clockwise me-1" />Refresh</>
          }
        </button>
      </div>

      {/* ── Filters ───────────────────────────────────────────────────── */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        <div className="input-group input-group-sm" style={{ maxWidth: 280 }}>
          <span className="input-group-text bg-white">
            <i className="bi bi-search text-muted" style={{ fontSize: "0.8rem" }} />
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Search label, details, or actor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="btn btn-outline-secondary" onClick={() => setSearch("")}>
              <i className="bi bi-x" />
            </button>
          )}
        </div>
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: 200 }}
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
        >
          {ACTION_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
        {distinctActors.length > 1 && (
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 200 }}
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
          >
            <option value="">All users</option>
            {distinctActors.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        )}
      </div>

      {/* ── Result count ──────────────────────────────────────────────── */}
      {!loading && (
        <p className="text-muted small mb-3">
          {filtered.length === 0
            ? "No entries match your filters."
            : `Showing ${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(safePage * PAGE_SIZE, filtered.length)} of ${filtered.length} entr${filtered.length === 1 ? "y" : "ies"}`
          }
          {entries.length >= 500 && (
            <span className="ms-1">(log capped at 500 entries)</span>
          )}
        </p>
      )}

      {/* ── Table ─────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="text-center py-5 text-muted">
          <div className="spinner-border mb-2" style={{ color: BRAND_GREEN }} role="status" />
          <p className="small mb-0">Loading audit log…</p>
        </div>
      ) : pageEntries.length === 0 ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5 text-muted">
            <i className="bi bi-journal-x" style={{ fontSize: "2.5rem", display: "block", marginBottom: "0.5rem", opacity: 0.4 }} />
            <p className="mb-0">
              {entries.length === 0 ? "No audit entries recorded yet." : "No entries match your filters."}
            </p>
          </div>
        </div>
      ) : (
        <div className="card border-0 shadow-sm" style={{ borderRadius: 10, overflow: "hidden" }}>
          <table className="table table-hover mb-0" style={{ fontSize: "0.85rem" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
                <th style={{ padding: "0.75rem 1rem", fontWeight: 600, color: "#495057", width: 44 }}></th>
                <th style={{ padding: "0.75rem 0.5rem", fontWeight: 600, color: "#495057" }}>Action</th>
                <th style={{ padding: "0.75rem 0.5rem", fontWeight: 600, color: "#495057" }}>Details</th>
                <th style={{ padding: "0.75rem 0.5rem", fontWeight: 600, color: "#495057" }}>By</th>
                <th style={{ padding: "0.75rem 1rem", fontWeight: 600, color: "#495057", whiteSpace: "nowrap" }}>Date &amp; Time</th>
              </tr>
            </thead>
            <tbody>
              {pageEntries.map((entry) => {
                const meta = ACTION_META[entry.action] || ACTION_META.default;
                const relative = formatRelative(entry.timestamp);
                return (
                  <tr key={entry.id}>
                    {/* Icon */}
                    <td style={{ padding: "0.75rem 0.5rem 0.75rem 1rem", verticalAlign: "middle" }}>
                      <div
                        style={{
                          width: 32, height: 32, borderRadius: "50%",
                          backgroundColor: meta.bg,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        <i className={`bi ${meta.icon}`} style={{ color: meta.color, fontSize: "0.85rem" }} />
                      </div>
                    </td>

                    {/* Label */}
                    <td style={{ padding: "0.75rem 0.5rem", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                      <span className="fw-semibold" style={{ color: "#212529" }}>{entry.label}</span>
                      {entry.id?.startsWith("hs-") && (
                        <span className="ms-2 badge" style={{ fontSize: "0.65rem", backgroundColor: "rgba(255,122,0,0.1)", color: "#b45309", fontWeight: 500 }}>HubSpot</span>
                      )}
                    </td>

                    {/* Details */}
                    <td style={{ padding: "0.75rem 0.5rem", verticalAlign: "middle", color: "#495057", maxWidth: 400, wordBreak: "break-word" }}>
                      {entry.details && <div>{entry.details}</div>}
                      {entry.changes && entry.changes.length > 0 && (
                        <div style={{ marginTop: entry.details ? "0.35rem" : 0, display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                          {entry.changes.map((c, i) => (
                            <div key={i} style={{ fontSize: "0.73rem", display: "flex", alignItems: "baseline", gap: "0.3rem", flexWrap: "wrap" }}>
                              <span style={{ color: "#6c757d", fontWeight: 600, whiteSpace: "nowrap" }}>{c.field}:</span>
                              <span style={{ color: "#b02a37", textDecoration: "line-through", opacity: 0.85 }}>{c.oldValue || "—"}</span>
                              <span style={{ color: "#6c757d" }}>→</span>
                              <span style={{ color: "#146c43", fontWeight: 500 }}>{c.newValue || "—"}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {!entry.details && (!entry.changes || entry.changes.length === 0) && (
                        <span className="text-muted">—</span>
                      )}
                    </td>

                    {/* Actor */}
                    <td style={{ padding: "0.75rem 0.5rem", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                      {entry.actor && entry.actor !== "Admin" ? (
                        <span className="badge rounded-pill" style={{ backgroundColor: "rgba(2,56,34,0.1)", color: BRAND_GREEN, fontWeight: 500 }}>
                          <i className="bi bi-person-fill me-1" style={{ fontSize: "0.7rem" }} />
                          {entry.actor}
                        </span>
                      ) : (
                        <span className="text-muted">Admin</span>
                      )}
                    </td>

                    {/* Timestamp */}
                    <td style={{ padding: "0.75rem 1rem", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                      <div style={{ fontSize: "0.82rem", color: "#495057" }}>{formatFull(entry.timestamp)}</div>
                      {relative && (
                        <div style={{ fontSize: "0.72rem", color: "#adb5bd", marginTop: "0.1rem" }}>{relative}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ────────────────────────────────────────────────── */}
      {!loading && totalPages > 1 && (
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3">
          <p className="text-muted small mb-0">
            Page {safePage} of {totalPages}
          </p>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              {/* Prev */}
              <li className={`page-item ${safePage === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={safePage === 1}
                  style={{ color: BRAND_GREEN }}
                >
                  <i className="bi bi-chevron-left" style={{ fontSize: "0.75rem" }} />
                </button>
              </li>

              {/* Numbered pages */}
              {pageNumbers().map((n, idx) =>
                n === "…" ? (
                  <li key={`ellipsis-${idx}`} className="page-item disabled">
                    <span className="page-link" style={{ color: "#adb5bd" }}>…</span>
                  </li>
                ) : (
                  <li key={n} className={`page-item ${n === safePage ? "active" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => setPage(n as number)}
                      style={n === safePage
                        ? { backgroundColor: BRAND_GREEN, borderColor: BRAND_GREEN, color: "#fff" }
                        : { color: BRAND_GREEN }
                      }
                    >
                      {n}
                    </button>
                  </li>
                )
              )}

              {/* Next */}
              <li className={`page-item ${safePage === totalPages ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={safePage === totalPages}
                  style={{ color: BRAND_GREEN }}
                >
                  <i className="bi bi-chevron-right" style={{ fontSize: "0.75rem" }} />
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default AuditLog;
